/*
 * product-phone-field.js
 *
 * Enforces the required "Mobile number" field rendered by
 * snippets/product-phone-field.liquid.
 *
 * How the block works, so nobody has to re-derive it:
 *
 *   product-form.js:20-22 starts its submit handler with
 *       evt.preventDefault();
 *       if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
 *
 *   So setting aria-disabled="true" on the Add to cart button is all that is
 *   needed to stop an add. No edit to product-form.js is required, and none
 *   should be made -- riding the theme's own gate keeps this upgrade-safe.
 *
 * Deliberate choice: the button is NOT disabled on page load. base.css:1336
 * renders aria-disabled="true" buttons at opacity 0.5, so pre-disabling would
 * make every class page load with a greyed-out, sold-out-looking Add to cart.
 * Instead the button looks normal and is blocked at the moment of the attempt,
 * with an inline message -- which is also exactly the behaviour described to
 * and approved by the client in LittlePawsYoga_PhoneAtCheckout_Options_v1.md.
 *
 * Quick add: quick-add.js clones the whole <product-info> element into the
 * modal and blanket-replaces the section id in the markup. Everything here is
 * therefore resolved relative to the element itself, never by rebuilding an id
 * string, so it works identically on the product page and in the modal.
 */

if (!customElements.get('phone-required-field')) {
  customElements.define(
    'phone-required-field',
    class PhoneRequiredField extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input[type="tel"]');
        this.errorWrapper = this.querySelector('.product-phone-field__error');
        this.errorText = this.errorWrapper?.querySelector('.error-message');
        this.productForm = this.closest('product-form');
        this.form = this.closest('form');

        if (!this.input || !this.form) return;

        // Matches product-form.js's own lookup, so we always act on the same
        // button it gates on.
        this.submitButton = this.productForm?.querySelector('[type="submit"]');

        // Guard against connectedCallback running twice (the element is moved
        // into the quick-add modal, which re-runs it).
        if (this.bound) return;
        this.bound = true;

        this.onInput = this.onInput.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.onSubmitCapture = this.onSubmitCapture.bind(this);
        this.onClickCapture = this.onClickCapture.bind(this);

        this.input.addEventListener('input', this.onInput);
        this.input.addEventListener('blur', this.onBlur);

        // Catches clicks on Add to cart before the form's submit handler runs.
        this.submitButton?.addEventListener('click', this.onClickCapture, true);

        // Belt and braces for Enter-key submits and anything else that submits
        // the form without a click.
        //
        // This is registered on `document`, not on the form. At the target
        // phase the capture flag is ignored and listeners fire in registration
        // order, so a capture listener on the form itself would NOT reliably
        // beat product-form.js's own submit listener. Capturing on an ancestor
        // does, because ancestor capture always precedes the target phase.
        document.addEventListener('submit', this.onSubmitCapture, true);
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this.onSubmitCapture, true);
      }

      /*
       * Validation is deliberately permissive: this store takes bookings from
       * interstate and overseas visitors, and a strict Australian-only pattern
       * would reject genuine customers. We only check the number is plausible.
       *
       * Strip everything that isn't a digit, treat a leading 00 as the
       * international prefix, then require 8-15 digits (15 is the E.164
       * maximum; 8 is the shortest realistic national number).
       */
      isValid(raw) {
        const value = (raw ?? '').trim();
        if (value === '') return false;

        // Reject anything containing letters -- catches "call me" and similar.
        if (/[a-z]/i.test(value)) return false;

        let digits = value.replace(/\D/g, '');
        if (digits.startsWith('00')) digits = digits.slice(2);

        return digits.length >= 8 && digits.length <= 15;
      }

      get isEmpty() {
        return (this.input.value ?? '').trim() === '';
      }

      showError() {
        const message = this.isEmpty
          ? this.dataset.errorEmpty || 'Please enter a mobile number.'
          : this.dataset.errorInvalid || 'Please check this phone number.';

        if (this.errorText) this.errorText.textContent = message;
        this.errorWrapper?.removeAttribute('hidden');
        this.input.setAttribute('aria-invalid', 'true');
      }

      clearError() {
        this.errorWrapper?.setAttribute('hidden', '');
        this.input.removeAttribute('aria-invalid');
      }

      block() {
        this.submitButton?.setAttribute('aria-disabled', 'true');
      }

      unblock() {
        // Only lift our own block. If the variant is genuinely sold out the
        // theme uses the separate `disabled` attribute, which we never touch.
        this.submitButton?.removeAttribute('aria-disabled');
      }

      onInput() {
        if (this.isValid(this.input.value)) {
          this.clearError();
          this.unblock();
        } else {
          this.block();
        }
      }

      onBlur() {
        // Don't nag someone who tabbed through the field without touching it.
        if (this.isEmpty) return;
        if (!this.isValid(this.input.value)) this.showError();
      }

      onClickCapture(event) {
        if (this.isValid(this.input.value)) return;

        event.preventDefault();
        event.stopPropagation();
        this.block();
        this.showError();
        this.input.focus();
      }

      onSubmitCapture(event) {
        // Only police our own form.
        if (event.target !== this.form) return;
        if (this.isValid(this.input.value)) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.block();
        this.showError();
        this.input.focus();
      }
    }
  );
}
